/**
 * ClientPicker — search-as-you-type client selector for the Agent Access
 * console (replaces the 145-row native <select>).
 *
 * Type to filter (name / company / email / the linked Google Ads account's name
 * or number); every row shows the client's linked Ads account so the owner can
 * see what an agent bound to them will be able to reach. ARIA editable
 * combobox: ↑/↓ move, Enter picks, Esc closes the list (and, while the list is
 * open, is swallowed so it does NOT also close the fullscreen console that
 * listens for Esc on the document), Tab leaves.
 */

import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { ClientOpt, formatAdsId, rankClients } from './agentAccessHelpers';

const MAX_SHOWN = 40;
/** Vertical room the list wants; below this we flip it upward if there's more room above. */
const LIST_ROOM = 290;

/** Stop an Escape keypress from reaching the document-level listener that
 *  closes the console. React 18 handles events at the root container, so a
 *  native stopPropagation here keeps it from ever reaching `document`. */
export function swallowEscape(e: React.KeyboardEvent): void {
  e.stopPropagation();
  (e.nativeEvent as any)?.stopImmediatePropagation?.();
}

export interface ClientPickerProps {
  clients: ClientOpt[];
  /** Selected client id, or '' for none. */
  value: string;
  onChange: (id: string) => void;
  ariaLabel: string;
  /** DOM id for the input, so an external <label htmlFor> can target it. */
  inputId?: string;
  disabled?: boolean;
  /** True until the customer list has loaded. */
  loading?: boolean;
  placeholder?: string;
  /** Border emphasis: 'ok' = bound, 'warn' = needs attention. */
  tone?: 'ok' | 'warn';
  title?: string;
}

const subline = (c: ClientOpt): string =>
  [c.company && c.company !== c.name ? c.company : '', c.email].filter(Boolean).join(' · ');

export const ClientPicker: React.FC<ClientPickerProps> = ({
  clients, value, onChange, ariaLabel, inputId, disabled, loading, placeholder = 'search clients…', tone, title,
}) => {
  const uid = useId();
  const listId = `${uid}-list`;
  const optId = (i: number) => `${uid}-o${i}`;
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [active, setActive] = useState(0);
  const [flip, setFlip] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selected = useMemo(() => clients.find((c) => c.id === value), [clients, value]);
  const { items, total } = useMemo(
    () => (open ? rankClients(clients, text, MAX_SHOWN) : { items: [] as ClientOpt[], total: 0 }),
    [open, clients, text],
  );

  const close = () => { setOpen(false); setText(''); };

  const openList = () => {
    if (disabled || open) return;
    const r = wrapRef.current?.getBoundingClientRect();
    if (r) {
      const below = window.innerHeight - r.bottom;
      setFlip(below < LIST_ROOM && r.top > below);
    }
    // Start on the current selection so ↓/Enter behave like a native select.
    const first = rankClients(clients, '', MAX_SHOWN).items;
    setActive(Math.max(0, first.findIndex((c) => c.id === value)));
    setText('');
    setOpen(true);
  };

  const choose = (c: ClientOpt) => { onChange(c.id); close(); };

  useEffect(() => {
    if (!open) return;
    document.getElementById(optId(active))?.scrollIntoView?.({ block: 'nearest' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, active]);

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const n = items.length;
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (!open) openList(); else if (n) setActive((a) => (a + 1) % n);
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (!open) openList(); else if (n) setActive((a) => (a - 1 + n) % n);
        break;
      case 'Enter':
        if (open) { e.preventDefault(); if (items[active]) choose(items[active]); }
        break;
      case 'Escape':
        if (open) { e.preventDefault(); swallowEscape(e); close(); }
        break;
      case 'Tab':
        close();
        break;
      default:
    }
  };

  // The closed field shows the selection; an id we can't resolve (customer
  // deleted, or the list hasn't loaded yet) is shown as-is rather than blanked.
  const shown = open ? text : (selected ? selected.name : (loading ? '' : value));
  const cls = ['aap-pick', open && 'is-open', flip && 'is-flip', disabled && 'is-disabled', tone && `aap-pick--${tone}`]
    .filter(Boolean).join(' ');

  return (
    <div
      ref={wrapRef}
      className={cls}
      onBlur={(e) => { if (!wrapRef.current?.contains(e.relatedTarget as Node | null)) close(); }}
    >
      <input
        ref={inputRef}
        id={inputId}
        type="text"
        className="aap-pick__input gads-bar__picker"
        role="combobox"
        aria-label={ariaLabel}
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={open && items[active] ? optId(active) : undefined}
        autoComplete="off"
        spellCheck={false}
        disabled={disabled}
        title={title}
        placeholder={loading ? 'loading clients…' : (open && selected ? selected.name : placeholder)}
        value={shown}
        onFocus={openList}
        onClick={openList}
        onChange={(e) => { setText(e.target.value); setActive(0); if (!open) setOpen(true); }}
        onKeyDown={onKeyDown}
      />
      {value && !disabled && !open && (
        <button type="button" className="aap-pick__clear" aria-label={`Clear ${ariaLabel.toLowerCase()}`}
          onClick={() => onChange('')}>×</button>
      )}
      {open && (
        <ul id={listId} role="listbox" aria-label={`${ariaLabel} results`} className="aap-pick__list"
          onMouseDown={(e) => e.preventDefault()}>
          {items.length === 0 && (
            <li className="aap-pick__empty" role="presentation">
              {loading ? 'loading clients…' : clients.length === 0 ? 'no clients found' : `No client matches “${text.trim()}”`}
            </li>
          )}
          {items.map((c, i) => (
            <li
              key={c.id}
              id={optId(i)}
              role="option"
              aria-selected={c.id === value}
              className={`aap-pick__opt${i === active ? ' is-active' : ''}${c.id === value ? ' is-selected' : ''}`}
              onMouseEnter={() => setActive(i)}
              onClick={() => choose(c)}
            >
              <span className="aap-pick__main">
                <span className="aap-pick__name">{c.name}</span>
                {subline(c) && <span className="aap-pick__sub">{subline(c)}</span>}
              </span>
              <span className={`aap-pick__ads${c.adsId ? ' has' : ''}`}>
                {c.adsId ? `Ads ${formatAdsId(c.adsId)}` : 'no Ads account'}
              </span>
            </li>
          ))}
          {total > items.length && (
            <li className="aap-pick__more" role="presentation">+{total - items.length} more — keep typing to narrow</li>
          )}
        </ul>
      )}
    </div>
  );
};

export default ClientPicker;
